import { useState, useEffect, useRef, useCallback } from 'react';
import type { ShowcaseData, ShowcaseScreen } from './useShowcaseData';

/* ═══════════════════════════════════════════════════════════════════════════
   useAssetPreloader — Orchestrates preloading of ALL showcase assets
   before playback begins.

   Collects every asset URL from every screen, preloads them in parallel,
   and reports progress. Returns `ready: true` when everything is cached.

   Asset types:
     1. Images — driver photos, sponsor logos, org branding, weather backgrounds
     2. GLB 3D models — car models for driver profile screens
     3. Lazy code chunks — CircuitInfo (deck.gl ~530KB)
     4. Custom fonts — org font face URL (WOFF2/TTF)
     5. Map pre-warm — flag for the HiddenMapPrewarmer component
   ═══════════════════════════════════════════════════════════════════════════ */

interface PreloadProgress {
  /** True when all assets are loaded and showcase is ready to play */
  ready: boolean;
  /** 0..1 progress fraction */
  progress: number;
  /** Current step label for the loading screen */
  label: string;
  /** Whether the showcase has a circuit screen needing map pre-warm */
  needsMapPrewarm: boolean;
  /** Circuit data for the map pre-warmer (null if no circuit screen) */
  mapPrewarmData: {
    trackPath: number[][];
    center: [number, number];
    mapStyleId: string;
    trackColor: string;
  } | null;
  /** Called by HiddenMapPrewarmer when tile pre-warming is complete */
  onMapPrewarmComplete: () => void;
}

/** Preload a single image by URL. Resolves when loaded or on error (non-blocking). */
function preloadImage(url: string): Promise<void> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve(); // Don't block on failed images
    img.src = url;
  });
}

/** Preload a GLB model via Three.js useGLTF.preload (dynamically imported). */
async function preloadGLB(url: string): Promise<void> {
  try {
    const { useGLTF } = await import('@react-three/drei');
    useGLTF.preload(url);
    // useGLTF.preload starts an async fetch but doesn't return a promise.
    // Wait a short time for the fetch to complete. For more reliable loading,
    // we fetch the file directly and let the browser cache handle it.
    const response = await fetch(url, { mode: 'cors' });
    if (response.ok) {
      // Read the body to ensure it's fully cached
      await response.arrayBuffer();
    }
  } catch {
    // Non-blocking — the 3D viewer will handle its own loading
  }
}

/** Preload a lazy-loaded code chunk by triggering its dynamic import. */
async function preloadChunk(importFn: () => Promise<unknown>): Promise<void> {
  try {
    await importFn();
  } catch {
    // Non-blocking
  }
}

/** Preload a custom font by loading it into the document fonts API. */
async function preloadFont(url: string, family: string): Promise<void> {
  try {
    const font = new FontFace(family, `url("${url}")`);
    const loaded = await font.load();
    document.fonts.add(loaded);
  } catch {
    // Non-blocking
  }
}

/* ---------- Asset Collection ---------- */

interface CollectedAssets {
  images: string[];
  glbModels: string[];
  hasCircuitScreen: boolean;
  hasLazyChunks: boolean;
  fontUrl: string | null;
  fontFamily: string | null;
  circuitData: PreloadProgress['mapPrewarmData'];
}

/** Extract all preloadable asset URLs from all screens in the showcase. */
function collectAssets(showcase: ShowcaseData): CollectedAssets {
  const images: string[] = [];
  const glbModels: string[] = [];
  let hasCircuitScreen = false;
  let circuitData: PreloadProgress['mapPrewarmData'] = null;

  // Org branding images
  if (showcase.organization?.logoUrl) images.push(showcase.organization.logoUrl);
  if (showcase.organization?.backgroundImageUrl) images.push(showcase.organization.backgroundImageUrl);
  if (showcase.themeOverrides?.logoUrl) images.push(showcase.themeOverrides.logoUrl);
  if (showcase.themeOverrides?.backgroundUrl) images.push(showcase.themeOverrides.backgroundUrl);

  // Font
  const fontFamily = showcase.organization?.fontFamily ?? null;
  const fontUrl = fontFamily && (/^https?:\/\//i.test(fontFamily) || /\.(woff2?|ttf|otf)([?#].*)?$/i.test(fontFamily))
    ? fontFamily
    : null;

  // Per-screen assets
  for (const screen of showcase.screens) {
    const config = screen.config ?? {};

    switch (screen.screenType) {
      case 'driver-profile':
      case 'driver-quote': {
        // Resolved driver data
        const driver = config._resolved_driver as Record<string, unknown> | undefined;
        if (driver) {
          if (driver.full_body_image_url) images.push(driver.full_body_image_url as string);
          if (driver.half_body_image_url) images.push(driver.half_body_image_url as string);
          if (driver.profile_image_url) images.push(driver.profile_image_url as string);
          if (driver.nationality) {
            images.push(`https://flagcdn.com/w80/${(driver.nationality as string).toLowerCase()}.png`);
          }
        }

        // Resolved car data
        const car = config._resolved_car as Record<string, unknown> | undefined;
        if (car) {
          if (car.model_3d_url) glbModels.push(car.model_3d_url as string);
          if (car.side_image_url) images.push(car.side_image_url as string);
          if (car.number_image_url) images.push(car.number_image_url as string);
        }

        // Resolved sponsors
        const sponsors = config._resolved_sponsors as Array<Record<string, unknown>> | undefined;
        if (sponsors) {
          for (const s of sponsors) {
            if (s.logo_dark_url) images.push(s.logo_dark_url as string);
            if (s.logo_light_url) images.push(s.logo_light_url as string);
          }
        }

        // Direct config car model URL
        if (config.carModelUrl) glbModels.push(config.carModelUrl as string);
        break;
      }

      case 'circuit': {
        hasCircuitScreen = true;
        const circuit = showcase.event?.circuit;
        if (circuit?.layout && circuit.layout.length > 2 && circuit.latitude != null && circuit.longitude != null) {
          // Compute track center for map pre-warming
          let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
          for (const [lat, lng] of circuit.layout) {
            if (lat < minLat) minLat = lat;
            if (lat > maxLat) maxLat = lat;
            if (lng < minLng) minLng = lng;
            if (lng > maxLng) maxLng = lng;
          }
          circuitData = {
            trackPath: circuit.layout,
            center: [(minLng + maxLng) / 2, (minLat + maxLat) / 2],
            mapStyleId: (config.mapStyle as string) || 'dark',
            trackColor: showcase.themeOverrides?.primaryColor ??
              showcase.organization?.primaryColor ?? '#6366f1',
          };
        }
        break;
      }

      default:
        break;
    }
  }

  // Country flag from event circuit
  const countryCode = showcase.event?.circuit?.countryCode;
  if (countryCode) {
    images.push(`https://flagcdn.com/w80/${countryCode.toLowerCase()}.png`);
  }

  // Deduplicate
  const uniqueImages = [...new Set(images)];
  const uniqueGLBs = [...new Set(glbModels)];

  return {
    images: uniqueImages,
    glbModels: uniqueGLBs,
    hasCircuitScreen,
    hasLazyChunks: hasCircuitScreen, // CircuitInfo is the only lazy chunk currently
    fontUrl,
    fontFamily: fontUrl ? 'RaceProXShowcaseOrgFont' : null,
    circuitData,
  };
}

/* ---------- Hook ---------- */

/**
 * Asset preloader for TV Showcase.
 *
 * Runs between the loading screen and playback start. Downloads ALL assets
 * for ALL screens so that every screen transition is instant and beautiful.
 */
export function useAssetPreloader(showcase: ShowcaseData | null): PreloadProgress {
  const [ready, setReady] = useState(false);
  const [progress, setProgress] = useState(0);
  const [label, setLabel] = useState('Preparing…');
  const [mapPrewarmDone, setMapPrewarmDone] = useState(false);
  const startedRef = useRef(false);

  const onMapPrewarmComplete = useCallback(() => {
    setMapPrewarmDone(true);
  }, []);

  const assets = showcase ? collectAssets(showcase) : null;

  useEffect(() => {
    if (!showcase || !assets || startedRef.current) return;
    startedRef.current = true;

    async function runPreload() {
      const tasks: Array<{ label: string; fn: () => Promise<void> }> = [];

      // 1. Fonts
      if (assets!.fontUrl && assets!.fontFamily) {
        tasks.push({
          label: 'Loading fonts…',
          fn: () => preloadFont(assets!.fontUrl!, assets!.fontFamily!),
        });
      }

      // 2. Images (batch as one task)
      if (assets!.images.length > 0) {
        tasks.push({
          label: 'Loading images…',
          fn: () => Promise.all(assets!.images.map(preloadImage)).then(() => {}),
        });
      }

      // 3. GLB models (each as a separate task — they're large)
      for (const url of assets!.glbModels) {
        tasks.push({
          label: 'Loading 3D models…',
          fn: () => preloadGLB(url),
        });
      }

      // 4. Lazy code chunks
      if (assets!.hasLazyChunks) {
        tasks.push({
          label: 'Preparing components…',
          fn: () => preloadChunk(() => import('../screens/CircuitInfo')),
        });
      }

      // If no circuit screen, we don't need to wait for map pre-warm
      if (!assets!.hasCircuitScreen) {
        setMapPrewarmDone(true);
      }

      const totalTasks = tasks.length;
      if (totalTasks === 0) {
        setProgress(1);
        setLabel('Ready');
        // If we also don't need map pre-warm, we're done
        if (!assets!.hasCircuitScreen) {
          setReady(true);
        }
        return;
      }

      let completed = 0;
      for (const task of tasks) {
        setLabel(task.label);
        await task.fn();
        completed++;
        // Reserve last 10% for map pre-warm if needed
        const staticProgress = assets!.hasCircuitScreen
          ? (completed / totalTasks) * 0.9
          : completed / totalTasks;
        setProgress(staticProgress);
      }

      if (!assets!.hasCircuitScreen) {
        setLabel('Ready');
        setProgress(1);
        setReady(true);
      } else {
        setLabel('Preparing maps…');
        // Map pre-warm progress will be completed by the HiddenMapPrewarmer
        // calling onMapPrewarmComplete after its timeout
      }
    }

    runPreload().catch(console.error);
  }, [showcase]); // eslint-disable-line react-hooks/exhaustive-deps

  // When map pre-warm completes, finalize
  useEffect(() => {
    if (mapPrewarmDone && startedRef.current && !ready) {
      setProgress(1);
      setLabel('Ready');
      // Brief pause so user sees "Ready" before playback starts
      const timer = setTimeout(() => setReady(true), 400);
      return () => clearTimeout(timer);
    }
  }, [mapPrewarmDone, ready]);

  return {
    ready,
    progress,
    label,
    needsMapPrewarm: assets?.hasCircuitScreen ?? false,
    mapPrewarmData: assets?.circuitData ?? null,
    onMapPrewarmComplete,
  };
}
